/**
 * Whether the signed-in user may open the Approval Desk — from
 * `GET /api/home/landing`.
 *
 * The desk is gated by the `approvals:desk` role (utils/permissions.ts
 * canOpenApprovalsDesk). Approval authority itself is identity-based — JA/JM/SM
 * on a job, line manager, delegate, Position Account, Manual of Authority — so
 * the server checks it and grants the role to an approver who lacks it. This
 * hook carries that answer for the session in which the role was granted,
 * before the signed-in user's role list catches up.
 *
 * One request per session, shared: the sidebar, the router and the Home page
 * all ask, and a module-level promise makes them share one round trip. Keyed on
 * the token so a different sign-in in the same tab asks again.
 *
 * Fails to `isApprover: false`. This decides where someone lands and whether
 * the desk opens — never what they may approve: each approve endpoint enforces
 * authority, and the existing approval screens do not depend on it.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { canOpenApprovalsDesk } from '../utils/permissions';

export interface LandingProfile {
  isApprover: boolean;
  reasons: string[];
  /** The server could not read every signal, so its "no" is not certain. */
  incomplete?: boolean;
  /** The request failed; `isApprover: false` is then a default, not an answer. */
  failed?: boolean;
  /** Read with `?fresh=1`, past the server's short cache of a "no". */
  fresh?: boolean;
}

interface LandingResponse {
  success?: boolean;
  data?: Partial<LandingProfile>;
}

const NOT_AN_APPROVER: LandingProfile = { isApprover: false, reasons: [] };

let cache: { token: string; promise: Promise<LandingProfile>; value?: LandingProfile } | null =
  null;

/** Hooks to tell when a re-check replaces the cached answer. */
const listeners = new Set<(token: string, p: LandingProfile) => void>();

function request(token: string, fresh: boolean): Promise<LandingProfile> {
  const entry: NonNullable<typeof cache> = {
    token,
    promise: axios
      .get<LandingResponse>(`/api/home/landing${fresh ? '?fresh=1' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(r => {
        const data = r?.data?.data;
        return {
          isApprover: data?.isApprover === true,
          reasons: Array.isArray(data?.reasons) ? data.reasons : [],
          incomplete: data?.incomplete === true,
          ...(fresh ? { fresh: true } : {}),
        };
      })
      .catch((): LandingProfile => ({ ...NOT_AN_APPROVER, failed: true, ...(fresh ? { fresh: true } : {}) }))
      .then(profile => {
        entry.value = profile;
        if (cache === entry) listeners.forEach(l => l(token, profile));
        return profile;
      }),
  };
  cache = entry;
  return entry.promise;
}

/** Fetch (or reuse) the landing profile for `token`. Never rejects. */
export function fetchLandingProfile(token: string): Promise<LandingProfile> {
  return cache?.token === token ? cache.promise : request(token, false);
}

/**
 * Ask again, past every cache, before acting on a "no": somebody who became
 * an approver in the last few minutes (an approval just routed to them) must
 * not be turned away by an answer given before it. Once per session — a fresh
 * answer is reused.
 */
export function recheckLandingProfile(token: string): Promise<LandingProfile> {
  if (cache?.token === token && (cache.value?.fresh || !cache.value)) return cache.promise;
  return request(token, true);
}

/** Forget the cached profile. For tests, and for a sign-out that does not reload. */
export function resetLandingProfileCache(): void {
  cache = null;
}

export interface UseLandingProfile extends LandingProfile {
  /** True until the profile for this token has resolved (or failed). */
  loading: boolean;
}

export function useLandingProfile(token: string | null | undefined): UseLandingProfile {
  const cached = token && cache?.token === token ? cache.value : undefined;
  const [profile, setProfile] = useState<LandingProfile | undefined>(cached);

  useEffect(() => {
    if (!token) {
      setProfile(undefined);
      return undefined;
    }
    let live = true;
    fetchLandingProfile(token).then(p => {
      if (live) setProfile(p);
    });
    const onRecheck = (t: string, p: LandingProfile) => {
      if (live && t === token) setProfile(p);
    };
    listeners.add(onRecheck);
    return () => {
      live = false;
      listeners.delete(onRecheck);
    };
  }, [token]);

  // No token yet is "not known yet", not "no": a render before sign-in has
  // settled must not send anyone away from a page.
  if (!token) return { ...NOT_AN_APPROVER, loading: true };
  const current = profile ?? cached;
  return current ? { ...current, loading: false } : { ...NOT_AN_APPROVER, loading: true };
}

/**
 * Whether the Approval Desk opens for this person: the approvals:desk role
 * (utils/permissions.ts), or a grant the server made this session, before the
 * signed-in role list has caught up. Every screen asks this, never the two
 * parts separately.
 */
export function useApprovalsDeskAccess(
  token: string | null | undefined,
  roles: string[]
): { allowed: boolean; landing: UseLandingProfile } {
  const landing = useLandingProfile(token);
  return { allowed: canOpenApprovalsDesk(roles) || landing.isApprover, landing };
}
